import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, Renderer2, ViewChild } from '@angular/core';
import { provideTranslocoScope, TranslocoService } from '@ngneat/transloco';
import { TranslocoLocaleService } from '@ngneat/transloco-locale';
import { Store } from '@ngxs/store';
import { BusinessModel, PricingInformation } from '@platform-frontend/marketplace';
import { YattaLangId } from '@platform-frontend/transloco';
import { TypedChanges } from '@platform-frontend/util';
import { CHECKOUT_TRANSLOCO_SCOPE } from '../../../checkout-transloco-scope';
import { ValidateApplePaySession } from '../../payment-wallet.actions';
import { WalletPriceInlineItemsService } from '../../wallet-price-inline-items.service';
import { WalletButtonEnvironment } from '../model/wallet-button-environment';
import { WalletButtonTextMode } from '../model/wallet-button-text-mode';

@Component({
  selector: 'yat-apple-pay-button',
  templateUrl: './apple-pay-button.component.html',
  styleUrls: ['./apple-pay-button.component.scss'],
  providers: [provideTranslocoScope(CHECKOUT_TRANSLOCO_SCOPE)]
})
export class ApplePayButtonComponent implements OnChanges {
  constructor(
    private store: Store,
    private renderer: Renderer2,
    private translocoService: TranslocoService,
    private translocoLocaleService: TranslocoLocaleService,
    private walletPriceInlineItemsService: WalletPriceInlineItemsService
  ) {}

  @ViewChild('applePayButton', { static: true }) applePayButton: ElementRef;

  @Input()
  buttonType: WalletButtonTextMode = 'BUY';

  @Input()
  buttonLanguage: YattaLangId;

  @Input()
  debitDate: Date | null;

  @Input()
  pricingInformation: PricingInformation;

  @Input()
  walletButtonEnvironment: WalletButtonEnvironment = 'LIVE';

  @Input()
  vendorName: string = '';

  @Input()
  businessModel: BusinessModel = 'B2C';

  @Output()
  loadPaymentData: EventEmitter<ApplePayJS.ApplePayPayment> = new EventEmitter<ApplePayJS.ApplePayPayment>();

  ngOnChanges(changes: TypedChanges<ApplePayButtonComponent>): void {
    if (changes.buttonLanguage || changes.buttonType) {
      this.updateButtonAttributes();
    }
  }

  private updateButtonAttributes(): void {
    if (this.applePayButton) {
      this.renderer.setAttribute(this.applePayButton.nativeElement, 'type', this.buttonType.toLowerCase());
      this.renderer.setAttribute(this.applePayButton.nativeElement, 'locale', this.buttonLanguage);
    }
  }

  onApplePayButtonClick(): void {
    const request: ApplePayJS.ApplePayPaymentRequest = {
      countryCode: 'DE',
      currencyCode: this.pricingInformation.currency,
      supportedNetworks: ['visa', 'mastercard', 'amex'],
      merchantCapabilities: ['supports3DS'],
      total: {
        label: this.translocoService.translate(
          this.debitDate === null ? 'paymentWallet.applePayButton.flyOutTitle' : 'paymentWallet.applePayButton.flyOutTitleDelayedPayment',
          {
            debitDate: this.debitDate ? this.translocoLocaleService.localizeDate(this.debitDate) : '',
            environment: this.walletButtonEnvironment === 'LIVE' ? 'LIVE' : 'DEMO',
            vendorName: this.vendorName
          },
          CHECKOUT_TRANSLOCO_SCOPE
        ),
        amount: this.pricingInformation.total.toFixed(2)
      },
      lineItems: this.walletPriceInlineItemsService.createApplePayLineItems(this.businessModel, this.pricingInformation)
    };

    const session = new ApplePaySession(14, request);

    session.onvalidatemerchant = () => {
      this.store.dispatch(new ValidateApplePaySession(this.pricingInformation, session));
    };

    session.onpaymentauthorized = (event: ApplePayJS.ApplePayPaymentAuthorizedEvent) => {
      session.completePayment(ApplePaySession.STATUS_SUCCESS);
      this.loadPaymentData.next(event.payment);
    };

    session.begin();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.code === 'Enter' || event.code === 'Space') {
      event.preventDefault();
      this.onApplePayButtonClick();
    }
  }
}
